import React from "react"
import {useTranslation} from "react-i18next"
import {Route, useLocation, useNavigate} from "react-router-dom"

import {Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText} from "@mui/material"

import {findWhere, first} from "underscore"

import Body from "../components/common/Body"
import Routes from "../configs/routeConfig"
import {localStorageItemName} from "../constants/local-storage-item-name"
import {RouteConfig, RoutePage} from "./@types/common"
import CommonUtils from "./Common"

// To filter authorized products based on configuration values
const authorizedProducts = "AuthorizedProducts_"
const Routing = {
    CreateMenu(role: boolean) {
        const {t} = useTranslation("menu")
        const navigate = useNavigate()
        const location = useLocation()

        const links = this.getLinks()

        return (
            <List id="menu" className="menuItemGroup">
                {role && links.map((link) => this.renderMenuItem(link, location, navigate, t))}
            </List>
        )
    },
    renderMenuItem(
        link: RoutePage,
        location: ReturnType<typeof useLocation>,
        navigate: ReturnType<typeof useNavigate>,
        t: any
    ) {
        const isSelected = this.isLinkSelected(link, location)
        return link.conditioned
            ? link.conditioned() && this.renderListItem(link, isSelected, navigate, t)
            : this.renderListItem(link, isSelected, navigate, t)
    },
    renderListItem(link: RoutePage, isSelected: boolean, navigate: ReturnType<typeof useNavigate>, t: any) {
        return (
            <ListItem
                key={link.name}
                disablePadding
                sx={{
                    display: "block",
                    paddingInline: 10,
                    borderRadius: 6,
                    paddingTop: 0
                }}
            >
                <ListItemButton
                    selected={isSelected}
                    sx={{minHeight: 48, justifyContent: "initial", px: 2.5}}
                    style={{padding: "10px 13px"}}
                    onClick={() => navigate(first(link.url.split(":")) || "")}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: 1,
                            justifyContent: "center",
                            color: "white"
                        }}
                    >
                        {link.icon}
                    </ListItemIcon>
                    <ListItemText
                        primary={t("menu:" + link.name)}
                        sx={{color: "white", fontWeight: "bolder", opacity: 1}}
                        className="MenuLabel"
                    />
                </ListItemButton>
            </ListItem>
        )
    },
    isLinkSelected(link: RoutePage, location: ReturnType<typeof useLocation>) {
        const linkBase = first(link.url.split(":")) || ""
        const linkSlug = linkBase.split("/").filter(Boolean)[1] ?? ""

        const isHomeOnDashBoard = link.name === "Home" && location.pathname.includes("/dashboard")

        if (isHomeOnDashBoard) {
            return true
        }

        const pathname = location.pathname.replace("_", "")
        const pathCategory = pathname.split("/").filter(Boolean)[1] ?? ""

        const isRootPath = location.pathname === "/"
        const matchesCategory = isRootPath ? linkBase === "/" : linkSlug !== "" && pathCategory === linkSlug

        const isDocumentDetailOfCycle = linkSlug !== "" && pathname.includes(`/documents/${linkSlug}/`)

        return matchesCategory || isDocumentDetailOfCycle
    },
    DisplayPage(page: RoutePage) {
        return page?.component
    },
    CreateRouting() {
        const links = this.getLinks(true)

        return (
            <>
                {links.map((link) => {
                    return (
                        <Route
                            key={link.name}
                            path={link.url}
                            element={
                                <Box
                                    component="main"
                                    sx={{flexGrow: 1, p: 0}}
                                    style={{
                                        height: "100vh",
                                        paddingInline:
                                            link.name === "AddEre" || link.IsOffline ? 0 : link.isOffline ? 0 : 16
                                    }}
                                >
                                    <Body page={link} />
                                </Box>
                            }
                        />
                    )
                })}

                <Route
                    element={
                        <Box component="main" sx={{flexGrow: 1, p: 0}} style={{height: "100vh", flex: 1}}>
                            <Body title="Erreur" />
                        </Box>
                    }
                />
            </>
        )
    },
    GetUrl(pageName: string) {
        const links = this.getLinks(true)
        const currentLink = findWhere(links, {name: pageName})
        return currentLink?.url || ""
    },
    GetUrls() {
        return this.getLinks()
    },
    GetCategory(url: string) {
        return url.split("/")[2]
    },
    IsOffline(url: string) {
        const links = this.getLinks(true).filter((link) => link.name !== "Home")

        if (url === "/") {
            return false
        } else {
            const currentPage = links.find((link) => url.includes(link.url.split(":")[0]))

            return currentPage?.IsOffline || false
        }
    },
    GoBack(navigate: ReturnType<typeof useNavigate>, params?: any) {
        navigate(-1)
    },
    getLinks(addHidedLink?: boolean) {
        const mapMainConfigValues = (): Map<string, boolean> => {
            const configValues = []
            const mainConfig = localStorage.getItem(localStorageItemName.mainConfig)
            if (mainConfig) {
                const result = JSON.parse(mainConfig)
                if (result) configValues.push(...result.configValues.filter(Boolean))
            }
            return new Map(configValues.map(({key, value}) => [key.toLowerCase(), value?.toLowerCase() === "true"]))
        }

        const currentProduct: string = CommonUtils.GetLocal("selectedProduct") || "nd"
        const productRouting: RouteConfig | undefined = findWhere(Routes.products, {
            id: currentProduct
        })
        const role = CommonUtils.GetLocal("selectedRole")
            ? JSON.parse(CommonUtils.GetLocal("selectedRole"))
            : {profile: "STANDARD_USER"}
        const userProfile: string = role.profile

        const configMap = mapMainConfigValues()
        const productLinks: RoutePage[] =
            productRouting?.pages
                .filter((item) => (item.roles ? item.roles.includes(userProfile) || item.roles.length === 0 : true))
                .filter((route) => {
                    const {natureMappingCodeFilterRoutesByBusinessConfig} = route
                    const key = authorizedProducts + natureMappingCodeFilterRoutesByBusinessConfig
                    const cfgFound = configMap.get(key?.toLowerCase() || "")
                    if (!natureMappingCodeFilterRoutesByBusinessConfig && !cfgFound) return true
                    return natureMappingCodeFilterRoutesByBusinessConfig && cfgFound
                }) || []

        const commonPages: RoutePage[] = Routes.commonPages.map((item) => ({
            name: item.name,
            url: item.url,
            icon: item.icon,
            component: item.component,
            isHidden: item.isHidden,
            roles: item.roles,
            isOffline: item.isOffline
        }))

        return commonPages
            .concat(productLinks)
            .filter((item) => (addHidedLink ? true : item.isHidden === undefined || !item.isHidden))
    }
}

export default Routing
